import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link2, Loader2, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import {
  attach835RemittancePayment,
  searchRemittanceClaims,
} from '../../services/api';
import type {
  RemittanceClaimSearchRow,
  RemittancePaymentDetail,
  RemittanceStatus,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { useToast } from '../../hooks/useToastContext';
import {
  formatRemittanceCurrency,
  formatRemittanceDateRange,
} from './remittance/remittanceFormatters';
import { RemittanceClaimContext } from './remittance/RemittanceClaimContext';

/** One page of candidates, matching the remittance worksheet's Add Claims dialog. */
const CANDIDATE_PAGE_SIZE = 25;

/** 'approved_for_submission' -> 'Approved for submission'. */
const formatCandidateStatus = (status: string): string => {
  const spaced = status.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Manually reconcile one unmatched 835 payment to a claim version (EMR-1409).
 *
 * The auto-match keys (PCN / payer claim control number) did not place this
 * payment, so the biller names the claim version it pays. The dialog loads the
 * agency's claim versions (any status — an 835 can pay OR deny a claim) and
 * shows each candidate's identity facts, so the biller picks one instead of
 * copying a claim id by hand. Typing the id stays as a fallback.
 *
 * The control shows on an unmatched payment of both Saved and Completed
 * advice: a Completed-advice attach posts immediately, while a Saved-advice
 * attach only links the claim and posts when the remittance is completed.
 *
 * The control hides entirely from users without `claims:reconcile` or
 * `billing:view` (EMR-2087 widened the backend attach gate to either slug,
 * matching the worksheet/candidate-search permission) — the backend attach
 * route refuses them the same action, and the same permission therefore
 * gates the candidate search. Hiding is UX; the route gate is the boundary
 * of truth. Matched payments (claim_id set) get no control: reassignment
 * is out of scope.
 */
export const AttachEvidenceAction: React.FC<{
  payment: RemittancePaymentDetail;
  /**
   * The remittance detail's status. Completed: the attach posts immediately.
   * Saved: the attach only links the claim, and the payment posts when the
   * remittance is completed.
   */
  adviceStatus: RemittanceStatus;
  /** Reload the remittance detail so the row shows its new match. */
  onAttached: () => void | Promise<void>;
}> = ({ payment, adviceStatus, onAttached }) => {
  // Selector shape 3 (frontend-navigation-gating): the store returns the
  // boolean, so a grant or revoke re-renders without another cause.
  // Either slug admits the attach (EMR-2087): the backend gate is
  // require_any_agency_permission([billing:view, claims:reconcile]).
  const canAttach = useStore(
    state =>
      state.hasPermission(PermissionSlug.CLAIMS_RECONCILE) ||
      state.hasPermission(PermissionSlug.BILLING_ACCESS)
  );
  const [open, setOpen] = useState(false);
  const [claimId, setClaimId] = useState('');
  const [busy, setBusy] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  // Applied (searched) filters, separate from the input drafts so typing does
  // not refetch; the Search button applies them.
  const [applied, setApplied] = useState({ patient: '', claim: '' });
  const [candidates, setCandidates] = useState<RemittanceClaimSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  // Latest-wins guard: a slow response for a superseded search must not
  // clobber the list shown for the current one.
  const requestRef = useRef(0);
  const { error: showError, success: showSuccess } = useToast();

  const search = useCallback(
    async (nextOffset = 0) => {
      const requestId = ++requestRef.current;
      setSearching(true);
      try {
        const resp = await searchRemittanceClaims({
          query: {
            patient_name: applied.patient || null,
            claim_number: applied.claim || null,
            status: 'all',
            limit: CANDIDATE_PAGE_SIZE,
            offset: nextOffset,
          },
        });
        if (requestId !== requestRef.current) return;
        if (resp.error || !resp.data) {
          // Never the raw server string: patient data travels in these
          // responses.
          console.error('Failed to load candidate claims:', resp.error);
          showError(
            getBusinessErrorMessage(resp) ?? 'Unable to load candidate claims.'
          );
          return;
        }
        setCandidates(resp.data.items);
        setOffset(nextOffset);
        setTotal(resp.data.total);
      } finally {
        if (requestId === requestRef.current) setSearching(false);
      }
    },
    [applied, showError]
  );

  // Candidates load when the dialog opens (and when a new search is applied),
  // so the biller sees the pick list without a first click. The button that
  // opens the dialog is claims:reconcile-or-billing:view-gated, so the fetch
  // is too.
  useEffect(() => {
    if (!open) return;
    void search(0);
  }, [open, search]);

  if (!canAttach || payment.claim_id != null) return null;

  const applyFilters = () => {
    setClaimId('');
    setApplied({ patient: patientName, claim: claimNumber });
  };

  const attach = async () => {
    setBusy(true);
    try {
      // The whole result, not just `.error`: the status lives on
      // `result.response`, and without it getBusinessErrorMessage cannot tell
      // a 5xx body from a 4xx business message and would show the server's
      // text.
      const resp = await attach835RemittancePayment({
        path: { payment_id: payment.id },
        body: { claim_id: claimId.trim() },
      });
      if (resp.error) throw resp;
    } catch (error) {
      // Never the raw server string: patient data travels in these
      // responses.
      console.error('Failed to attach the payment to a claim:', error);
      showError(
        getBusinessErrorMessage(error) ??
          'Unable to attach this payment to a claim.'
      );
      setBusy(false);
      return;
    }

    showSuccess(
      adviceStatus === 'saved'
        ? 'Linked to claim — posts on completion'
        : 'Attached to claim'
    );
    setOpen(false);
    setClaimId('');
    setBusy(false);
    try {
      await onAttached();
    } catch (error) {
      console.error('Failed to reload the remittance detail:', error);
      showError('Attached. Refresh the page to see the updated match.');
    }
  };

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => setOpen(true)}
      >
        <Link2 className='h-4 w-4' />
        Attach to claim
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-h-[85vh] max-w-2xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Attach to claim</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Link this payment to the claim version it pays. Pick a candidate
            below, or enter a claim id by hand.
          </p>

          {/* The advice's own facts stay put while the biller picks, so the
              match is made against the incoming claim, not a candidate. */}
          <RemittanceClaimContext payment={payment} />

          <div className='grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end'>
            <Input
              value={patientName}
              onChange={e => setPatientName(e.target.value)}
              aria-label='Patient name'
              placeholder='Patient name'
              onKeyDown={e => {
                if (e.key === 'Enter') applyFilters();
              }}
            />
            <Input
              value={claimNumber}
              onChange={e => setClaimNumber(e.target.value)}
              aria-label='Claim number or PCN'
              placeholder='Claim # / PCN'
              onKeyDown={e => {
                if (e.key === 'Enter') applyFilters();
              }}
            />
            <Button
              type='button'
              variant='outline'
              onClick={applyFilters}
              disabled={searching}
            >
              <Search className='h-4 w-4' />
              Search
            </Button>
          </div>

          <div className='overflow-hidden rounded-md border'>
            {searching ? (
              <div className='flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Loading candidate claims…
              </div>
            ) : candidates.length === 0 ? (
              <p className='p-4 text-sm text-muted-foreground'>
                No candidate claims match your search.
              </p>
            ) : (
              <ul
                aria-label='Candidate claims'
                className='max-h-72 divide-y overflow-y-auto'
              >
                {candidates.map(claim => {
                  const selected = claimId === claim.id;
                  return (
                    <li key={claim.id}>
                      <button
                        type='button'
                        onClick={() => setClaimId(claim.id)}
                        aria-pressed={selected}
                        className={`flex w-full flex-col gap-1 p-3 text-left transition-colors hover:bg-accent/40 ${
                          selected ? 'bg-accent/60' : ''
                        }`}
                      >
                        <span className='flex min-w-0 flex-wrap items-center gap-2'>
                          <span className='min-w-0 break-words text-sm font-medium'>
                            Claim {claim.claim_number ?? '—'}
                          </span>
                          <Badge
                            variant='outline'
                            className='whitespace-nowrap text-xs'
                          >
                            {formatCandidateStatus(claim.status)}
                          </Badge>
                          {selected ? (
                            <Badge className='whitespace-nowrap text-xs'>
                              Selected
                            </Badge>
                          ) : null}
                        </span>
                        <span className='break-words text-xs text-muted-foreground'>
                          {claim.patient_name} · PCN{' '}
                          {claim.patient_control_number ?? '—'} · Claim id{' '}
                          {claim.id}
                        </span>
                        <span className='text-xs text-muted-foreground'>
                          Service period{' '}
                          {formatRemittanceDateRange(
                            claim.service_period_start,
                            claim.service_period_end
                          )}
                        </span>
                        <span className='text-xs text-muted-foreground'>
                          Cycle{' '}
                          {claim.billing_cycle_id
                            ? claim.billing_cycle_id.slice(0, 8)
                            : '—'}{' '}
                          · Created{' '}
                          {claim.created_date
                            ? formatDateToMMDDYYYY(claim.created_date)
                            : '—'}{' '}
                          ··Charges{' '}
                          {formatRemittanceCurrency(claim.total_charges)}{' '}
                          ··Payer {claim.payor_name}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {total > CANDIDATE_PAGE_SIZE && (
            <div className='flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground'>
              <span>
                {offset + 1}–{offset + candidates.length} of {total}
              </span>
              <div className='flex gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    void search(Math.max(0, offset - CANDIDATE_PAGE_SIZE))
                  }
                  disabled={searching || offset === 0}
                >
                  Previous
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => void search(offset + CANDIDATE_PAGE_SIZE)}
                  disabled={searching || offset + candidates.length >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Advanced fallback: the biller can still name the claim version id
              directly (the identifier shown on the claim timeline). */}
          <div className='space-y-1'>
            <Input
              value={claimId}
              onChange={e => setClaimId(e.target.value)}
              aria-label='Claim id'
              placeholder='Claim id (manual entry)'
              className='font-mono text-xs'
            />
            <p className='text-xs text-muted-foreground'>
              The claim id is on the claim&rsquo;s timeline in Billing.
            </p>
          </div>

          <div className='flex flex-col gap-2 sm:flex-row sm:justify-end'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={() => void attach()}
              disabled={busy || claimId.trim() === ''}
            >
              Attach
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttachEvidenceAction;
